import { Command } from './Templater';

export interface IReplacer<ContentType, ReturnType> {
  /**
   * This regExp will be used by the templater to determine if it should call
   * the parse function or not.
   */
  match: RegExp;
  /**
   * The parse method should take the RegExpMatchArray and the value of the
   * original attribute and return whatever the replacer is intended for.
   * */
  parse: (result: RegExpMatchArray, value: ContentType) => ReturnType;
}

/**
 * The Attribute parsers are intended to parse some attributes whose names
 * match in one of the following array items' match property. If the match
 * property matches the attribute name, the parse function will be called with
 * the result of the match and the original Value passed to that attribute.
 * This way, it can be modified not only the name but the value as well.
 */
const AttributeParsers: IReplacer<
  string,
  { newName: string; newValue: unknown }[] | null
>[] = [
  /**
   * Allows to pass javascript event handlers through the format
   * onEventDispatched, the main difficulty is that the html parser returns the
   * attributes with lowercase, that's why any attribute of the type
   * onEventDispatched will be passed to the component as onEventdispatched
   * (note the lowercase 'd').
   */
  {
    match: /^(?:data-)?on(.*)$/i,
    parse([, originalAction], value) {
      if (value.match(/^__contextMethod/))
        return [
          {
            newName: `on${originalAction[0].toUpperCase()}${originalAction.slice(
              1,
            )}`,
            newValue: value,
          },
        ];

      let newName = `on${originalAction
        .charAt(0)
        .toUpperCase()}${originalAction.slice(1)}`;

      const composedEvent = originalAction.match(/^(mouse|key|double)(.*)$/i);
      if (composedEvent) {
        newName = `on${composedEvent[1]
          .charAt(0)
          .toUpperCase()}${composedEvent[1].slice(1)}${composedEvent[2]
          .charAt(0)
          .toUpperCase()}${composedEvent[2].slice(1)}`;
      }

      return [
        {
          newName,
          newValue: (ev: unknown) => {
            const evalString = value.replace(/return [^;\n\r]+/, '');

            const f = eval(evalString) as (ev: unknown) => void;

            f(ev);
          },
        },
      ];
    },
  },
  {
    /**
     * Allows to pass style objects as JSON strings
     */
    match: /^data-style$/,
    parse(_match, value) {
      try {
        return [{ newName: 'style', newValue: JSON.parse(value) }];
      } catch (e) {
        console.error({ value, e });
      }
      return null;
    },
  },
  {
    /**
     * Allows conditional rendering of a component
     */
    match: /^data-render$/,
    parse(_match, originalValue) {
      if (originalValue !== '' && !JSON.parse(originalValue)) {
        throw new Command('COMMAND: PREVENT_RENDERING');
      }
      return [
        {
          newName: 'data-render',
          newValue: undefined,
        },
      ];
    },
  },
  {
    /**
     * Allow to pass json data as property to the components
     */
    match: /^data-json-([a-zA-Z][\w_]*)$/,
    parse: ([, name], originalValue) => {
      try {
        return [
          {
            newName: name,
            newValue: JSON.parse(originalValue) as unknown,
          },
        ];
      } catch (e) {
        console.info(`Prop: ${name}, originalValue: ${originalValue}`);
        console.error(e);
        return null;
      }
    },
  },
  {
    match: /^data(?:-dom)?-variant$/,
    parse(_, newValue) {
      return [
        { newName: 'data-variant', newValue },
        { newName: 'variant', newValue },
      ];
    },
  },
  {
    match: /^data-dom-([a-zA-Z][\w_]*)/i,
    parse([, name], newValue) {
      return [{ newName: `data-${name}`, newValue }];
    },
  },
  {
    /**
     * Change all the attributes whose name matches with data-(name) to just
     * name
     */
    match: /^data-([a-zA-Z][\w_]*)$/,
    parse: ([, newName], newValue) => [{ newName, newValue }],
  },
  /**
   * Allows to pass conditional data to a component. It accepts as value a JSON
   * string with the following structure:
   *
   * data-condition-NAME_OF_ATTRIBUTE=`
   * {
   *  "condition": CONDITION,
   *  "NAME_OF_ATTRIBUTE": VALUE_TO_PASS_TO_THE_COMPONENT
   * }
   *
   * Where:
   *  - NAME_OF_ATTRIBUTE can be anything you want, the only limitations are
   *  the attribute names limitations. - CONDITION will be evaluated with a
   *  ternary conditional, if it parses to true the value will be passed or
   *  hidden elsewhere - VALUE_TO_PASS_TO_THE_COMPONENT Since all the attribute
   *  will be parsed with JSON, the value to pass can be anything that
   *      can be parsed this way.
   */
  {
    match: /^data-condition-([a-zA-Z][\w_]*)$/,
    parse: ([, newName], value) => {
      try {
        const parsedValue = JSON.parse(value) as Record<string, unknown>;
        return [
          {
            newName,
            newValue: parsedValue.condition ? parsedValue[newName] : undefined,
          },
        ];
      } catch (e) {
        console.error(e);
      }
      return null;
    },
  },
  {
    match: /^tabindex/i,
    parse(_, newValue) {
      return [{ newName: 'tabIndex', newValue }];
    },
  },
];

export default AttributeParsers;
